import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Matter from "matter-js";
import Reveal from "@/components/Reveal";
import { RefreshCw, MousePointer2 } from "lucide-react";

const CHIPS = [
  { label: "WEB DEV", variant: "orange", size: "lg" },
  { label: "MARKETING", variant: "ghost", size: "lg" },
  { label: "SEO", variant: "lime", size: "md" },
  { label: "AEO", variant: "orange", size: "md" },
  { label: "GEO", variant: "ghost", size: "md" },
  { label: "BRAND SYSTEMS", variant: "ghost", size: "lg" },
  { label: "PERFORMANCE", variant: "orange", size: "md" },
  { label: "DESIGN", variant: "ghost", size: "md" },
  { label: "FAST.", variant: "lime", size: "sm" },
  { label: "INKAA.", variant: "orange", size: "lg" },
  { label: "24H REPLY", variant: "ghost", size: "sm" },
  { label: "SHIPPED", variant: "ghost", size: "sm" },
];

const variantClass = (v) => {
  if (v === "orange")
    return "bg-[#FF4500] text-black border border-[#FF4500]";
  if (v === "lime") return "bg-[#CCFF00] text-black border border-[#CCFF00]";
  return "bg-[#0F0F0F] text-white border border-white/15";
};
const sizeClass = (s) => {
  if (s === "lg") return "px-7 py-4 text-3xl md:text-4xl";
  if (s === "md") return "px-5 py-3 text-xl md:text-2xl";
  return "px-4 py-2.5 text-sm md:text-base";
};

export default function PhysicsPlayground() {
  const sceneRef = useRef(null);
  const chipRefs = useRef([]);
  const enginesRef = useRef(null);
  const [bump, setBump] = useState(0);
  const [draggingId, setDraggingId] = useState(null);

  useEffect(() => {
    const sceneEl = sceneRef.current;
    if (!sceneEl) return undefined;

    const w = sceneEl.clientWidth || 800;
    const h = sceneEl.clientHeight || 500;

    const {
      Engine,
      Runner,
      Bodies,
      Body,
      Composite,
      Mouse,
      MouseConstraint,
      Events,
    } = Matter;

    const engine = Engine.create();
    engine.gravity.x = 0;
    engine.gravity.y = 1;
    engine.gravity.scale = 0.0012;
    engine.positionIterations = 10;
    engine.velocityIterations = 8;
    engine.constraintIterations = 4;
    const world = engine.world;

    const wallOpts = { isStatic: true, render: { visible: false } };
    const wallThickness = 200;
    const walls = [
      Bodies.rectangle(w / 2, -wallThickness / 2, w + wallThickness * 2, wallThickness, wallOpts),
      Bodies.rectangle(w / 2, h + wallThickness / 2, w + wallThickness * 2, wallThickness, wallOpts),
      Bodies.rectangle(-wallThickness / 2, h / 2, wallThickness, h + wallThickness * 2, wallOpts),
      Bodies.rectangle(w + wallThickness / 2, h / 2, wallThickness, h + wallThickness * 2, wallOpts),
    ];
    Composite.add(world, walls);

    const bodies = chipRefs.current.map((el, i) => {
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const cw = rect.width;
      const ch = rect.height;
      const x = Math.max(cw / 2 + 10, Math.min(w - cw / 2 - 10, Math.random() * w));
      const y = 60 + Math.random() * (h * 0.4);
      const body = Bodies.rectangle(x, y, cw, ch, {
        chamfer: { radius: ch / 2 },
        restitution: 0.55,
        friction: 0.05,
        frictionAir: 0.018,
        density: 0.0018,
      });
      Body.setAngularVelocity(body, (Math.random() - 0.5) * 0.06);
      Body.setAngle(body, (Math.random() - 0.5) * 0.4);
      return body;
    });
    const validBodies = bodies.filter(Boolean);
    Composite.add(world, validBodies);

    const mouse = Mouse.create(sceneEl);
    mouse.pixelRatio = 1;
    const mouseConstraint = MouseConstraint.create(engine, {
      mouse,
      constraint: { stiffness: 0.6, damping: 0.05, render: { visible: false } },
    });
    Composite.add(world, mouseConstraint);

    // Allow page scroll on wheel within scene (matter blocks wheel by default)
    if (mouse.element) {
      mouse.element.removeEventListener("wheel", mouse.mousewheel);
      mouse.element.removeEventListener("DOMMouseScroll", mouse.mousewheel);
    }

    Events.on(mouseConstraint, "startdrag", (e) => {
      const idx = validBodies.indexOf(e.body);
      if (idx >= 0) setDraggingId(idx);
    });
    Events.on(mouseConstraint, "enddrag", () => setDraggingId(null));

    const updateChips = () => {
      validBodies.forEach((body, i) => {
        const el = chipRefs.current[i];
        if (!el) return;
        // Velocity cap to prevent tunneling
        const maxV = 30;
        if (Math.abs(body.velocity.x) > maxV || Math.abs(body.velocity.y) > maxV) {
          Matter.Body.setVelocity(body, {
            x: Math.max(-maxV, Math.min(maxV, body.velocity.x)),
            y: Math.max(-maxV, Math.min(maxV, body.velocity.y)),
          });
        }
        // Safety net: if a body has escaped, teleport it back
        if (
          body.position.x < -100 ||
          body.position.x > w + 100 ||
          body.position.y < -200 ||
          body.position.y > h + 200
        ) {
          Matter.Body.setPosition(body, { x: w / 2, y: 80 });
          Matter.Body.setVelocity(body, { x: 0, y: 0 });
          Matter.Body.setAngularVelocity(body, 0);
        }
        el.style.transform = `translate(${body.position.x}px, ${body.position.y}px) translate(-50%, -50%) rotate(${body.angle}rad)`;
      });
    };
    Events.on(engine, "afterUpdate", updateChips);

    const runner = Runner.create();
    Runner.run(runner, engine);

    enginesRef.current = { engine, runner, bodies: validBodies, Body };

    const onResize = () => {
      const nw = sceneEl.clientWidth;
      const nh = sceneEl.clientHeight;
      Body.setPosition(walls[0], { x: nw / 2, y: -wallThickness / 2 });
      Body.setPosition(walls[1], { x: nw / 2, y: nh + wallThickness / 2 });
      Body.setPosition(walls[2], { x: -wallThickness / 2, y: nh / 2 });
      Body.setPosition(walls[3], { x: nw + wallThickness / 2, y: nh / 2 });
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      Runner.stop(runner);
      Events.off(engine, "afterUpdate", updateChips);
      Events.off(mouseConstraint);
      Composite.clear(world, false);
      Engine.clear(engine);
      enginesRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bump]);

  // re-run physics when window resizes drastically (rebuild)
  useEffect(() => {
    let last = window.innerWidth;
    const onResize = () => {
      if (Math.abs(window.innerWidth - last) > 200) {
        last = window.innerWidth;
        setBump((b) => b + 1);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const reset = () => {
    const ctx = enginesRef.current;
    if (!ctx) return;
    const { Body } = ctx;
    const sceneEl = sceneRef.current;
    if (!sceneEl) return;
    const w = sceneEl.clientWidth;
    ctx.bodies.forEach((body, i) => {
      const x = Math.random() * (w - 100) + 50;
      const y = 60 + Math.random() * 120;
      Body.setPosition(body, { x, y });
      Body.setVelocity(body, { x: (Math.random() - 0.5) * 6, y: 0 });
      Body.setAngularVelocity(body, (Math.random() - 0.5) * 0.06);
      Body.setAngle(body, (Math.random() - 0.5) * 0.4);
    });
  };

  return (
    <section
      className="relative py-24 md:py-32 ink-grain"
      data-testid="physics-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <Reveal>
              <span className="eyebrow">Play with us</span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl lg:text-7xl mt-5">
                Grab. Throw. <br />
                <span className="text-stroke">Watch them</span>{" "}
                <span className="text-[#FF4500]">collide.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-5 max-w-xl text-white/70 text-lg">
                Every block is something we ship. Drag them around — we'll wait.
                Then{" "}
                <a href="/contact" className="link-underline text-[#FF4500] font-semibold">
                  start a project
                </a>{" "}
                and we'll do the same with your competition.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.18}>
            <button
              onClick={reset}
              className="btn-ghost"
              data-testid="physics-reset-button"
            >
              <RefreshCw className="h-4 w-4" /> Reset
            </button>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            ref={sceneRef}
            className="relative w-full h-[520px] md:h-[600px] rounded-3xl border border-white/10 bg-[#0a0a0a] overflow-hidden select-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 50% 100%, rgba(255,69,0,0.08), transparent 60%), linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)",
              backgroundSize: "100% 100%, 60px 60px, 60px 60px",
            }}
            data-testid="physics-scene"
          >
            {/* Hint */}
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-white/45 pointer-events-none">
              <MousePointer2 className="h-3 w-3" />
              Click & drag the blocks
            </div>

            {/* Chips */}
            {CHIPS.map((c, i) => (
              <span
                key={c.label}
                ref={(el) => (chipRefs.current[i] = el)}
                className={`absolute top-0 left-0 font-display font-bold uppercase tracking-tight rounded-full whitespace-nowrap shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)] transition-shadow duration-150 ${variantClass(
                  c.variant
                )} ${sizeClass(c.size)} ${
                  draggingId === i ? "shadow-[0_0_0_4px_rgba(255,69,0,0.25)]" : ""
                }`}
                style={{
                  transform: "translate(-9999px, -9999px)",
                  willChange: "transform",
                  cursor: "grab",
                  userSelect: "none",
                  touchAction: "none",
                }}
                data-cursor-hover
                data-testid={`physics-chip-${i}`}
              >
                {c.label}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
